import {
  Component,
  ComponentRef,
  effect,
  ElementRef,
  input,
  output,
  viewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SmartSearchTagComponent } from '../smart-search-tag/smart-search-tag.component';
import { SmartSearchService } from '../../shared/services/smart-search.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Period } from '../../common-explain/models/filter-date';
import { SmartSearchPromptService } from '../../shared/services/smart-search-prompt.service';
import { Prompt } from '../../models/prompt';

type TagKey = 'impacter' | 'perimeter' | 'subject' | 'period' | 'company';

interface TagMatch {
  key: string;
  index: number;
}

const TAGS_REGISTRY: Record<string, { type: TagKey; frenchLabel: string }> = {
  '[impacter]': { type: 'impacter', frenchLabel: 'Nom de la personne' },
  '[perimeter]': { type: 'perimeter', frenchLabel: 'Périmètre géographique' },
  '[subject]': { type: 'subject', frenchLabel: 'Sujet' },
  '[period]': { type: 'period', frenchLabel: 'Période' },
  '[company]': { type: 'company', frenchLabel: 'Entreprise' },
};

@Component({
  selector: 'app-smart-search-prompt',
  templateUrl: './smart-search-prompt.component.html',
  styleUrl: './smart-search-prompt.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SmartSearchPromptComponent {
  editor = viewChild('editor', { read: ElementRef });
  copyTooltip = viewChild<NgbTooltip>('copyTooltip');
  prompt = input<string>();
  isEditable = input<boolean>(true);
  allowCopy = input<boolean>(true);
  allowSave = input<boolean>(true);
  usePreviewTags = input<boolean>(false);
  showAvailableTags = input<boolean>(true);
  showRelaunchButton = input<boolean>(false);
  promptChange = output<string>();
  relaunchAnalysis = output<{ prompt: string; promptWithValues: string; tagsUserInputs: Record<string, string> }>();
  tagsRefs: {
    [k in TagKey]: {
      icon: string;
      placeholder?: string;
      inputPlaceholder?: string;
      tooltip?: string;
      display?: string;
      content?: string;
      value?: string;
      ref?: ComponentRef<SmartSearchTagComponent>;
    };
  } = {
    perimeter: {
      icon: 'ph-fill ph-map-pin',
      placeholder: 'search.smart-search-prompt.perimeter-placeholder',
      inputPlaceholder: 'search.smart-search-prompt.perimeter-input-placeholder',
      tooltip: 'search.smart-search-prompt.perimeter-tag-tooltip',
    },
    impacter: {
      icon: 'ph-fill ph-user',
      placeholder: 'search.smart-search-prompt.impacter-placeholder',
      inputPlaceholder: 'search.smart-search-prompt.impacter-input-placeholder',
    },
    subject: {
      icon: 'ph-fill ph-text-t',
      placeholder: 'search.smart-search-prompt.subject-placeholder',
      inputPlaceholder: 'search.smart-search-prompt.subject-input-placeholder',
    },
    period: {
      icon: 'ph-fill ph-calendar',
      placeholder: 'search.smart-search-prompt.period-placeholder',
      inputPlaceholder: 'search.smart-search-prompt.period-input-placeholder',
      tooltip: 'search.smart-search-prompt.period-tag-tooltip',
    },
    company: {
      icon: 'ph-fill ph-buildings',
      placeholder: 'search.smart-search-prompt.company-placeholder',
      inputPlaceholder: 'search.smart-search-prompt.company-input-placeholder',
    },
  };
  example =
    '\n\n- Réalise cette analyse sur le périmètre suivant : [perimeter] \n' +
    '- Réalise cette analyse sur la période suivante : [period] \n';

  constructor(
    private vcr: ViewContainerRef,
    private smartSearchService: SmartSearchService,
    private smartSearchPromptService: SmartSearchPromptService,
    protected promptService: SmartSearchPromptService
  ) {
    effect(() => {
      const prompt = this.prompt();
      const editor = this.editor();
      if (prompt && editor) {
        this.deserialize(prompt);
      }
    });
    effect(async () => {
      const context = smartSearchService.context();
      if (context.fullPerimeter) {
        this.tagsRefs.perimeter.content = context.fullPerimeter;
      }
      if (context.displayedPerimeter) {
        this.tagsRefs.perimeter.display = context.displayedPerimeter;
      }
      if (context.impacterId) {
        this.tagsRefs.impacter.content = context.impacterName ?? undefined;
      }
      if (context.promptInputs?.subject) {
        this.tagsRefs.subject.value = context.promptInputs.subject;
        this.tagsRefs.subject.display = context.promptInputs.subject;
      }
      if (context.promptInputs?.impacter) {
        this.tagsRefs.impacter.value = context.promptInputs.impacter;
        this.tagsRefs.impacter.display = context.promptInputs.impacter;
      }
      if (context.promptInputs?.company) {
        this.tagsRefs.company.value = context.promptInputs.company;
        this.tagsRefs.company.display = context.promptInputs.company;
      }
      if (context.period?.from && context.period?.to) {
        const periodValue = this.formatPeriod(context.period);
        this.tagsRefs.period.content = periodValue;
        this.tagsRefs.period.display = periodValue;
      } else {
        const allPeriodsText = 'Toute la période';
        this.tagsRefs.period.content = allPeriodsText;
        this.tagsRefs.period.display = allPeriodsText;
      }
      Object.keys(this.tagsRefs).forEach(key => this.updateTagRef(key as TagKey));
    });
  }

  private findNextTagMatch(text?: string | null): TagMatch | null {
    if (!text) {
      return null;
    }

    const regex = /\[[^\]]+]/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const key = match[0];
      if (TAGS_REGISTRY[key]) {
        return { key, index: match.index ?? 0 };
      }
    }

    return null;
  }

  private replaceTags(node: Text): Text {
    let current = node;
    let match = this.findNextTagMatch(current.textContent);

    while (match) {
      current = this.insertTag(current, match.index, match.key);
      match = this.findNextTagMatch(current.textContent);
    }

    return current;
  }

  onInput(event: Event) {
    const selection = getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const node = range.startContainer;

    if ((event as InputEvent).inputType === 'insertFromPaste') {
      this.replaceTagsInEditor();
      return;
    }
    const content = (this.editor()?.nativeElement as HTMLElement)?.innerText || '';
    if (content.trim().length === 1) {
      document.execCommand('insertText', false, this.example);
      selection.setPosition(node, 1);
      this.replaceTagsInEditor();
      return;
    }

    if (node.nodeType !== Node.TEXT_NODE) {
      return;
    }

    const match = this.findNextTagMatch(node.textContent);
    if (match) {
      this.insertTag(node as Text, match.index, match.key, true, range);
    }
  }

  onCopy(event: ClipboardEvent) {
    const selection = getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const parts: string[] = [];
    for (let i = 0; i < selection.rangeCount; i++) {
      parts.push(this.serialize(selection.getRangeAt(i), false, true));
    }

    const clipboardText = parts.join('');
    if (!clipboardText) {
      return;
    }

    event.preventDefault();
    event.clipboardData?.setData('text/plain', clipboardText);
  }

  onCut(event: ClipboardEvent) {
    this.onCopy(event);
    document.execCommand('delete');
  }

  onPaste(e: ClipboardEvent) {
    const text = e.clipboardData?.getData('text/plain');
    if (!text) {
      return;
    }

    e.preventDefault();
    const textWithTags = this.convertFrenchLabelsToTags(text);
    document.execCommand('insertText', false, textWithTags);
    this.replaceTagsInEditor();
  }

  private convertFrenchLabelsToTags(text: string): string {
    let convertedText = text;
    Object.entries(TAGS_REGISTRY).forEach(([tag, tagInfo]) => {
      const frenchLabelPattern = `\\[${tagInfo.frenchLabel}\\]`;
      const regex = new RegExp(frenchLabelPattern, 'g');
      convertedText = convertedText.replace(regex, tag);
    });
    return convertedText;
  }

  replaceTagsInEditor() {
    const editor = this.editor()?.nativeElement;
    if (!editor) {
      return;
    }

    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null);

    let textNode = walker.nextNode() as Text | null;
    while (textNode) {
      this.replaceTags(textNode);
      textNode = walker.nextNode() as Text | null;
    }
  }

  insertTag(node: Text, index: number, key: string, replace = true, range?: Range, scrollTo = false) {
    const tag = TAGS_REGISTRY[key];

    const after = node.splitText(index);
    const rest = after.splitText(replace ? key.length : 0);

    const tagConfig = this.tagsRefs[tag.type];
    const ref = tagConfig.ref || this.vcr.createComponent(SmartSearchTagComponent);
    tagConfig.ref = ref;
    if (!this.isEditable()) {
      ref.setInput('removable', false);
      ref.setInput('displayTooltip', false);
      ref.setInput('editable', false);
    }
    this.updateTagRef(tag.type);
    const element = ref.location.nativeElement as HTMLElement;
    element.contentEditable = 'false';
    element.dataset['tag'] = tag.type;
    element.dataset['placeholder'] = key;

    after.parentNode?.replaceChild(element, after);

    if (range) {
      range.setStartAfter(element);
      range.collapse(true);

      const sel = getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    if (scrollTo) element.scrollIntoView(false);

    return rest;
  }

  serialize(range?: Range, withValues = false, translateTags = false): string {
    // Return editor content as plain text
    const source = range ? range.cloneContents() : (this.editor()?.nativeElement.cloneNode(true) as Node);
    if (!source) {
      return '';
    }
    this.replacePlaceholderNodes(source as HTMLElement, withValues, translateTags);

    return this.collectTextWithDivs(source);
  }

  deserialize(value: string): void {
    // Convert and add content to editor
    const editor = this.editor();
    if (!editor) {
      return;
    }

    const root = editor.nativeElement as HTMLElement;
    root.innerHTML = '';

    const normalized = value.replace(/\r\n|\n|\r/g, '\n');
    const lines = normalized.split('\n');
    lines.forEach(line => {
      const container = document.createElement('div');
      if (line) {
        const textNode = document.createTextNode(line);
        container.appendChild(textNode);
        this.replaceTags(textNode);
      } else {
        container.appendChild(document.createElement('br'));
      }
      root.appendChild(container);
    });

    if (!root.hasChildNodes()) {
      root.appendChild(document.createElement('div'));
    }
  }

  hasEmptyTags() {
    const editor = this.editor();
    return Object.values(this.tagsRefs).some(
      config => editor?.nativeElement.contains(config.ref?.location.nativeElement) && !config.ref?.instance.value()
    );
  }

  getTagsUserInputs(): { [key: string]: string } {
    const editor = this.editor();
    return Object.entries(this.tagsRefs).reduce((prev, [key, config]) => {
      if (!config.ref || config.content || !editor?.nativeElement.contains(config.ref?.location.nativeElement)) {
        return prev;
      }
      return { ...prev, [key]: config.ref.instance.value() };
    }, {});
  }

  onRelaunchAnalysis(): void {
    const tagsUserInputs = this.getTagsUserInputs();

    this.relaunchAnalysis.emit({
      prompt: this.serialize(undefined, false),
      promptWithValues: this.serialize(undefined, true),
      tagsUserInputs: tagsUserInputs,
    });
  }

  canTriggerSmartSearch(): boolean {
    const hasPrompt = this.hasPromptContent();

    if (!hasPrompt) {
      return false;
    }
    return !this.hasEmptyTags();
  }

  getTooltipMessage(): string {
    if (!this.hasPromptContent()) {
      return 'search.smart-search-panel.specify-analysis';
    }
    if (this.hasEmptyTags()) {
      return 'search.smart-search-panel.fill-tags';
    }
    return '';
  }

  private formatPeriod(period: Period): string {
    const fromDate = new Date(period.from);
    const toDate = new Date(period.to);
    const fromFormatted = fromDate.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const toFormatted = toDate.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return `du ${fromFormatted} au ${toFormatted}`;
  }

  copyPrompt(): void {
    navigator.clipboard
      .writeText(this.serialize(undefined, false, true))
      .then(() => {
        const tooltip = this.copyTooltip();
        if (tooltip) {
          tooltip.open();
          setTimeout(() => {
            tooltip.close();
          }, 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy prompt:', err);
      });
  }

  private replacePlaceholderNodes(root: HTMLElement, withValues = false, translateTags = false): void {
    root.querySelectorAll<HTMLElement>('[data-placeholder]').forEach(node => {
      const placeholder = node.dataset['placeholder'];
      let content = placeholder;
      if (placeholder && withValues) {
        const key = TAGS_REGISTRY[placeholder].type;
        content = this.tagsRefs[key].ref?.instance.value();
      } else if (placeholder && translateTags) {
        const tagInfo = TAGS_REGISTRY[placeholder];
        content = tagInfo ? `[${tagInfo.frenchLabel}]` : placeholder;
      }
      node.replaceWith(document.createTextNode(content || ''));
    });
  }

  private collectTextWithDivs(root: Node): string {
    let text = '';

    const ensureNewLine = () => {
      if (!text) {
        return;
      }
      if (!text.endsWith('\n')) {
        text += '\n';
      }
    };

    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? '';
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toUpperCase();

        if (tagName === 'DIV') {
          ensureNewLine();
          element.childNodes.forEach(walk);
          ensureNewLine();
          return;
        }

        if (tagName === 'BR') {
          text += '\n';
          return;
        }
      }

      node.childNodes.forEach(walk);
    };

    walk(root);

    if (text.endsWith('\n')) {
      text = text.slice(0, -1);
    }

    return text;
  }

  private updateTagRef(key: TagKey) {
    const config = this.tagsRefs[key];
    if (config.ref) {
      config.ref.setInput('icon', config.icon);
      config.ref.setInput('placeholder', config.placeholder);
      if (this.usePreviewTags()) {
        config.ref.setInput('content', config.placeholder);
        config.ref.setInput('display', config.placeholder);
        return;
      }
      config.ref.setInput('inputPlaceholder', config.inputPlaceholder);
      config.ref.setInput('tooltip', config.tooltip);
      config.ref.setInput('display', config.display);
      config.ref.setInput('content', config.content);
      if (config.value) {
        config.ref.setInput('value', config.value);
      }
    }
  }

  protected availableTags() {
    const editorNode = this.editor()?.nativeElement as HTMLElement;
    return Object.entries(this.tagsRefs).filter(([key, config]) => !editorNode.contains(config.ref?.location.nativeElement));
  }

  protected hasPromptContent(): boolean {
    const content = (this.editor()?.nativeElement as HTMLElement)?.innerText || '';
    return content.trim().length > 0;
  }

  canAddTagToNode(node: Node) {
    const editorNode = this.editor()?.nativeElement as HTMLElement;
    const tagsRefsNodes = Object.values(this.tagsRefs)
      .filter(config => config.ref)
      .map(ref => ref.ref?.location.nativeElement as HTMLElement);
    return editorNode.contains(node) && !tagsRefsNodes.some(tagNode => tagNode.contains(node));
  }

  addTag(tagKey: string) {
    const range = this.getCursorRange();
    if (range) {
      this.addTagAtCursor(tagKey);
    } else {
      this.addTagAtEnd(tagKey);
    }
  }

  private getCursorRange(): Range | null {
    const selection = getSelection();
    const editorNode = this.editor()?.nativeElement as HTMLElement;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorNode.contains(range.startContainer) && this.canAddTagToNode(range.startContainer)) {
        return range;
      }
    }
    return null;
  }

  addTagAtCursor(tagKey: string) {
    const selection = getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    let node = range.startContainer;
    let index = range.startOffset;
    const editorNode = this.editor()?.nativeElement as HTMLElement;

    if (node.isEqualNode(editorNode) || node.nodeType !== Node.TEXT_NODE) {
      node = node.appendChild(new Text(' '));
      index = 0;
    }
    this.insertTag(node as Text, index, `[${tagKey}]`, false, range, true);
  }

  private addTagAtEnd(tagKey: string) {
    const editorNode = this.editor()?.nativeElement as HTMLElement;
    const walker = document.createTreeWalker(editorNode, NodeFilter.SHOW_TEXT);
    let lastTextNode: Text | null = null;
    while (walker.nextNode()) {
      lastTextNode = walker.currentNode as Text;
    }

    const textNode = lastTextNode || editorNode.appendChild(document.createTextNode(' '));
    const range = document.createRange();
    range.setStart(textNode, textNode.textContent?.length || 0);
    range.collapse(true);
    this.insertTag(textNode, textNode.textContent?.length || 0, `[${tagKey}]`, false, range, true);
  }

  saveNewPrompt() {
    const ref = this.promptService.savePrompt(this.serialize());
    ref.result.then((prompt: { id?: number; title: string; text: string }) => {
      const newPrompt = new Prompt({ ...prompt, user_id: Number(localStorage.getItem('user_id') ?? undefined) });
      this.smartSearchPromptService.savePromptUsage(newPrompt);
      this.smartSearchPromptService.addPrompt(newPrompt);
    });
  }
}
